// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { confirmEMRPatient } from "../api";

export const useConfirmEMRPatient = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (payload: { emrJobId: string }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return confirmEMRPatient(token, clinicId, payload.emrJobId);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unassigned-emr", clinicId] });
      queryClient.invalidateQueries({ queryKey: ["external-exams"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Patient confirmed successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Patient could not be confirmed!"),
      });
    },
  });
};
